import FeedbackSettingsDialog from 'components/CreateAssignment/FeedbackSettingsDialog';
import InlineCommentsSettingsDialog from 'components/CreateAssignment/InlineCommentsSettingsDialog';
import StudentFeedbackSettingsDialog from 'components/CreateAssignment/StudentFeedbackSettingsDialog';
import { ADMIN_OVERRIDE_STATE } from 'components/Settings';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import userService from 'services/user';
import { assignmentDefaultsFromApi, assignmentDefaultsToApi } from 'utils/assignmentDefaults';

export const PreferenceContext = createContext(null);

export const usePreferenceSettings = () => useContext(PreferenceContext);

const FEEDBACK_SETTING_KEYS = Object.freeze({
    feedback: { settingsKey: 'feedbackSettings', contextKey: 'feedbackCustomContext' },
    formative: { settingsKey: 'studentFeedbackSettings', contextKey: 'studentFeedbackCustomContext' },
});

const getFeedbackSettingsValues = ({
    structure,
    numSuggestions,
    suggestionLength,
    languageLevel,
    tone,
    customInstructions,
}) => ({
    structure,
    numSuggestions,
    suggestionLength,
    languageLevel,
    tone,
    customInstructions,
});

export const PreferenceProvider = ({ children, onDirtyChange }) => {
    const { isGradingFeatureDisabled, isFeedbackFeatureDisabled, isInlineCommentsFeatureDisabled } = useApp();
    const { auth } = useAuth();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const userId = auth?.user_id;
    const [defaults, setDefaults] = useState(() => assignmentDefaultsFromApi(null));
    const defaultsRef = useRef(defaults);
    const [saving, setSaving] = useState(false);
    const [feedbackDialog, setFeedbackDialog] = useState(null);
    const [inlineDialog, setInlineDialog] = useState(false);
    const queue = useRef(Promise.resolve());
    const saveVersion = useRef(0);
    const loadedUserIdRef = useRef(null);

    const replaceDefaults = useCallback((next) => {
        defaultsRef.current = next;
        setDefaults(next);
    }, []);

    useEffect(() => {
        if (!userId || loadedUserIdRef.current === userId) return;

        const loadPreferences = async () => {
            loadedUserIdRef.current = userId;
            setFeedbackDialog(null);
            setInlineDialog(false);
            try {
                const preferences = await userService.getAssignmentDefaults(userId);
                if (loadedUserIdRef.current !== userId) return;
                replaceDefaults(assignmentDefaultsFromApi(preferences));
            } catch (error) {
                // The original product defaults remain the safe fallback.
                if (loadedUserIdRef.current !== userId) return;
                replaceDefaults(assignmentDefaultsFromApi(null));
            }
        };

        loadPreferences();
    }, [replaceDefaults, userId]);

    const adminOverrideStates = useMemo(
        () => ({
            grading: isGradingFeatureDisabled ? ADMIN_OVERRIDE_STATE.OVERRIDDEN : ADMIN_OVERRIDE_STATE.MAY_OVERRIDE,
            feedback: isFeedbackFeatureDisabled ? ADMIN_OVERRIDE_STATE.OVERRIDDEN : ADMIN_OVERRIDE_STATE.MAY_OVERRIDE,
            'formative-feedback': isFeedbackFeatureDisabled
                ? ADMIN_OVERRIDE_STATE.OVERRIDDEN
                : ADMIN_OVERRIDE_STATE.MAY_OVERRIDE,
            'inline-comments': isInlineCommentsFeatureDisabled
                ? ADMIN_OVERRIDE_STATE.OVERRIDDEN
                : ADMIN_OVERRIDE_STATE.MAY_OVERRIDE,
        }),
        [isFeedbackFeatureDisabled, isGradingFeatureDisabled, isInlineCommentsFeatureDisabled]
    );

    const save = useCallback(
        (next) => {
            replaceDefaults(next);
            onDirtyChange?.(true);
            const version = ++saveVersion.current;
            queue.current = queue.current.then(async () => {
                setSaving(true);
                try {
                    await userService.updateAssignmentDefaults(userId, assignmentDefaultsToApi(next));
                    message.success(t('common:message.preferences-updated', 'Preferences have been updated.'));
                    if (saveVersion.current === version) onDirtyChange?.(false);
                } catch (error) {
                    message.error(t('message.something-went-wrong', 'Something went wrong.'));
                } finally {
                    setSaving(false);
                }
            });
            return queue.current;
        },
        [message, onDirtyChange, replaceDefaults, t, userId]
    );

    const update = useCallback((key, value) => save({ ...defaultsRef.current, [key]: value }), [save]);

    const saveFeedbackSettings = useCallback(
        (next, type) => {
            const { settingsKey, contextKey } = FEEDBACK_SETTING_KEYS[type];
            const currentDefaults = defaultsRef.current;
            save({
                ...currentDefaults,
                [settingsKey]: {
                    ...currentDefaults[settingsKey],
                    ...getFeedbackSettingsValues(next),
                },
                [contextKey]: next[contextKey] || '',
            });
            setFeedbackDialog(null);
        },
        [save]
    );

    const value = useMemo(
        () => ({
            defaults,
            saving,
            update,
            save,
            feedbackDialog,
            setFeedbackDialog,
            inlineDialog,
            setInlineDialog,
            isGradingFeatureDisabled,
            isFeedbackFeatureDisabled,
            isInlineCommentsFeatureDisabled,
            adminOverrideStates,
        }),
        [
            defaults,
            feedbackDialog,
            inlineDialog,
            isFeedbackFeatureDisabled,
            isGradingFeatureDisabled,
            isInlineCommentsFeatureDisabled,
            adminOverrideStates,
            save,
            saving,
            update,
        ]
    );

    const feedbackSettings =
        feedbackDialog === 'formative' ? defaults.studentFeedbackSettings : defaults.feedbackSettings;
    const feedbackForm = {
        ...defaults,
        rubric: null,
        feedbackSettings,
        feedbackCustomContext:
            feedbackDialog === 'formative' ? defaults.studentFeedbackCustomContext : defaults.feedbackCustomContext,
    };

    return (
        <PreferenceContext.Provider value={value}>
            {children}
            <FeedbackSettingsDialog
                isTemplatedFeedbackAvailable={false}
                open={feedbackDialog === 'feedback'}
                form={feedbackForm}
                onClose={() => setFeedbackDialog(null)}
                onSave={(next) => saveFeedbackSettings(next, 'feedback')}
            />
            <StudentFeedbackSettingsDialog
                isTemplatedFeedbackAvailable={false}
                open={feedbackDialog === 'formative'}
                form={feedbackForm}
                onClose={() => setFeedbackDialog(null)}
                showAssignmentSettingsCopy={false}
                onSave={(next) => saveFeedbackSettings(next, 'formative')}
            />
            <InlineCommentsSettingsDialog
                open={inlineDialog}
                form={defaults}
                onClose={() => setInlineDialog(false)}
                onSave={(next) => {
                    const currentDefaults = defaultsRef.current;
                    save({
                        ...currentDefaults,
                        inlineCommentsSettings: {
                            ...currentDefaults.inlineCommentsSettings,
                            maxCommentsPerPage: next.maxCommentsPerPage,
                            commentLength: next.commentLength,
                            languageLevel: next.languageLevel,
                            tone: next.tone,
                        },
                        inlineCommentsCustomContext: next.inlineCommentsCustomContext || '',
                    });
                    setInlineDialog(false);
                }}
            />
        </PreferenceContext.Provider>
    );
};
